import React from "react";
import {
  formatFileSize,
  getDeliveryStatus,
  getMessageButtons,
  getMessageCaption,
  getMessageContacts,
  getMessageFilename,
  getMessageLocation,
  getMessageLocationLink,
  getMessageReaction,
  getMessageSize,
  getMessageTemplate,
  isMediaMessageType,
  parseMessagePayload,
  resolveMessageType,
  WA_STATUS_LABEL
} from "../../../utils/whatsappMessages";

/**
 * Renders the content bubble(s) of one chat row for both layouts:
 * "in" (incoming, white/gray) and "out" (outgoing, purple #640180).
 *
 * The canonical msgtype wins; when the API only sends the legacy mediatype
 * the type is derived from it, so nothing that used to render disappears.
 */

// DIAG temporario (remover depois): identifica qual item/componente renderiza cada bolha.
const WA_RENDER_TAG = "wa-render-diag-v4";
const WA_RENDER_SEEN = new Set();

const VARIANT = {
  in: {
    row: "d-flex flex-row justify-content-end flex-end",
    wrapper: "float-end w-auto",
    bubble: "#e7e7e7",
    text: "#212529",
    link: "#640180",
    muted: "#6e747c",
    subtitle: "text-end"
  },
  out: {
    row: "d-flex flex-row justify-content-start",
    wrapper: "w-auto",
    bubble: "#640180",
    text: "#ffffff",
    link: "#f6e9fb",
    muted: "#e2cdea",
    subtitle: ""
  }
};

function Tick({ color, double }) {
  return (
    <svg viewBox="0 0 18 12" width="16" height="11" fill="none" stroke={color} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M1 6.4 L4.1 9.5 L10.8 2.4" />
      {double && <path d="M7 9.5 L13.6 2.4" />}
    </svg>
  );
}

function FailedTick({ color }) {
  return (
    <svg viewBox="0 0 12 12" width="12" height="12" fill="none" stroke={color} strokeWidth="1.4" strokeLinecap="round" aria-hidden="true">
      <circle cx="6" cy="6" r="5" />
      <path d="M6 3.4 L6 6.6" />
      <path d="M6 8.4 L6 8.5" />
    </svg>
  );
}

/** Delivery ticks driven only by the wastatus column/MQTT topic. */
export function MessageStatus({ item }) {
  const status = getDeliveryStatus(item);

  if (!status) {
    return null;
  }

  const color = status === "failed" ? "#e53935" : status === "read" ? "#4fc3f7" : "#9aa0a6";

  return (
    <span
      className="d-inline-flex align-items-center ms-1"
      title={WA_STATUS_LABEL[status] || status}
      data-wastatus={status}
    >
      {status === "failed" ? <FailedTick color={color} /> : <Tick color={color} double={status !== "sent"} />}
    </span>
  );
}

function Bubble({ theme, children, channel, keyName }) {
  return (
    <div className={theme.row} style={{ overflow: "hidden" }} key={keyName}>
      <div className={theme.wrapper}>
        <div
          className="small py-2 px-3 mb-1 rounded-3"
          style={{
            backgroundColor: theme.bubble,
            color: theme.text,
            maxWidth: channel === "media" ? "240px" : "100%",
            overflowWrap: "break-word"
          }}
        >
          {children}
        </div>
      </div>
    </div>
  );
}

function FileMeta({ theme, filename, size }) {
  if (!filename && !size) {
    return null;
  }

  return (
    <div className="mt-1" style={{ color: theme.muted, fontSize: 10, wordBreak: "break-word" }}>
      {filename || "file"}
      {size ? " - " + formatFileSize(size) : ""}
    </div>
  );
}

function DocumentIcon({ color }) {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke={color} strokeWidth="1.6" aria-hidden="true">
      <path d="M14 2.5H6.5A2 2 0 0 0 4.5 4.5v15a2 2 0 0 0 2 2h11a2 2 0 0 0 2-2V8z" />
      <path d="M14 2.5V8h5.5" />
    </svg>
  );
}

function LocationIcon({ color }) {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke={color} strokeWidth="1.6" aria-hidden="true">
      <path d="M12 21.5s7-6.02 7-11.5a7 7 0 1 0-14 0c0 5.48 7 11.5 7 11.5z" />
      <circle cx="12" cy="10" r="2.6" />
    </svg>
  );
}

function MediaImage({ theme, media, msgtype, alt }) {
  const [failed, setFailed] = React.useState(false);

  if (failed) {
    return <span style={{ color: theme.muted }}>[imagem indisponivel]</span>;
  }

  return (
    <a href={media} target="_blank" rel="noopener noreferrer">
      <img
        className="img-fluid rounded-3"
        src={media}
        alt={alt}
        onError={() => setFailed(true)}
        style={{ maxWidth: msgtype === "sticker" ? "160px" : "100%" }}
      />
    </a>
  );
}

export default function MessageContent({ item, variant = "in", index = 0 }) {
  const theme = VARIANT[variant] || VARIANT.in;
  const msgtype = resolveMessageType(item);
  const text = typeof item?.message === "string" ? item.message : "";
  const caption = getMessageCaption(item);
  const filename = getMessageFilename(item);
  const size = getMessageSize(item);
  const media = item?.media;

  // DIAG temporario (remover depois)
  try {
    const key = variant + "|" + String(item?.id) + "|" + String(item?.userid) + "|" + String(item?.message || "").slice(0, 24);

    if (!WA_RENDER_SEEN.has(key)) {
      WA_RENDER_SEEN.add(key);
      console.log("WA-RENDER", WA_RENDER_TAG, JSON.stringify({
        variant,
        id: item?.id,
        userid: item?.userid,
        waid: item?.waid ? "yes" : null,
        createdat: item?.createdat,
        msgtype: item?.msgtype,
        msg: String(item?.message || "").slice(0, 50)
      }));
    }
  } catch (error) {
    console.log("WA-RENDER erro", error);
  }
  const blocks = [];

  const isPlainText = msgtype === "text" || msgtype === "system" || msgtype === "order";

  if ((isPlainText || msgtype === "unknown") && text.trim()) {
    blocks.push(
      <Bubble key="text" theme={theme} keyName={index + "text"} channel={isPlainText ? "text" : "unknown"}>
        <span style={{ whiteSpace: "pre-wrap" }}>{text}</span>
      </Bubble>
    );
  }

  if (msgtype === "image" || msgtype === "sticker") {
    blocks.push(
      <Bubble key="image" theme={theme} keyName={index + "image"} channel="media">
        {media ? (
          <MediaImage theme={theme} media={media} msgtype={msgtype} alt={item?.mediatype || msgtype} />
        ) : (
          <span style={{ color: theme.muted }}>[imagem indisponivel]</span>
        )}
        {filename ? <FileMeta theme={theme} filename={filename} size={size} /> : null}
      </Bubble>
    );
  }

  if (msgtype === "video") {
    blocks.push(
      <Bubble key="video" theme={theme} keyName={index + "video"} channel="media">
        {media ? (
          <video controls preload="metadata" className="img-fluid rounded-3" style={{ maxWidth: "100%" }}>
            <source src={media} type={item?.mediatype || undefined} />
            <a href={media} target="_blank" rel="noopener noreferrer">Download video</a>
          </video>
        ) : (
          <span style={{ color: theme.muted }}>[video unavailable]</span>
        )}
        <FileMeta theme={theme} filename={filename} size={size} />
      </Bubble>
    );
  }

  if (msgtype === "audio") {
    blocks.push(
      <Bubble key="audio" theme={theme} keyName={index + "audio"} channel="media">
        {media ? (
          <audio controls style={{ maxWidth: "100%" }}>
            <source src={media} type={item?.mediatype || undefined} />
            <a href={media} target="_blank" rel="noopener noreferrer">Download audio</a>
          </audio>
        ) : (
          <span style={{ color: theme.muted }}>[audio unavailable]</span>
        )}
        <FileMeta theme={theme} filename={filename} size={size} />
      </Bubble>
    );
  }

  if (msgtype === "document") {
    blocks.push(
      <Bubble key="document" theme={theme} keyName={index + "document"} channel="media">
        <div className="d-flex flex-row align-items-center">
          <DocumentIcon color={theme.text} />
          <div className="ms-2" style={{ wordBreak: "break-word" }}>
            {media ? (
              <a href={media} target="_blank" rel="noopener noreferrer" style={{ color: theme.link, textDecoration: "underline" }}>
                {filename || "Download document"}
              </a>
            ) : (
              <span>{filename || "document"}</span>
            )}
            {size ? <div style={{ color: theme.muted, fontSize: 10 }}>{formatFileSize(size)}</div> : null}
          </div>
        </div>
      </Bubble>
    );
  }

  if (msgtype === "location") {
    const location = getMessageLocation(item);
    blocks.push(
      <Bubble key="location" theme={theme} keyName={index + "location"} channel="location">
        <div className="d-flex flex-row align-items-center">
          <LocationIcon color={theme.text} />
          <a
            className="ms-2"
            href={getMessageLocationLink(location)}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: theme.link, textDecoration: "underline" }}
          >
            {location?.name || "Shared location"}
          </a>
        </div>
        {location?.address ? <div style={{ color: theme.muted, fontSize: 10 }}>{location.address}</div> : null}
        {location ? <div style={{ color: theme.muted, fontSize: 10 }}>{location.latitude + ", " + location.longitude}</div> : null}
      </Bubble>
    );
  }

  if (msgtype === "contacts") {
    const contacts = getMessageContacts(item);
    blocks.push(
      <Bubble key="contacts" theme={theme} keyName={index + "contacts"} channel="contacts">
        {contacts.map((contact, contactIndex) => (
          <div className="d-flex flex-row align-items-center" key={"contact" + contactIndex}>
            <div className="d-flex flex-column">
              <span className="fw-bold">{contact.name || contact.phone || "contact"}</span>
              {contact.name && contact.phone ? <span style={{ color: theme.muted, fontSize: 10 }}>{contact.phone}</span> : null}
            </div>
            {contact.vcard ? (
              <a
                className="ms-2"
                href={"data:text/vcard;charset=utf-8," + encodeURIComponent(contact.vcard)}
                download={(contact.name || "contact").replace(/[^a-z0-9_-]/gi, "_") + ".vcf"}
                style={{ color: theme.link, fontSize: 10, textDecoration: "underline" }}
              >
                vCard
              </a>
            ) : null}
          </div>
        ))}
        {contacts.length === 0 ? <span style={{ color: theme.muted }}>[contact unavailable]</span> : null}
      </Bubble>
    );
  }

  if (msgtype === "template") {
    const template = getMessageTemplate(item);
    blocks.push(
      <Bubble key="template" theme={theme} keyName={index + "template"} channel="template">
        <div style={{ fontSize: 10, color: theme.muted }}>
          {"Template" + (template.name ? " - " + template.name : "") + (template.language ? " (" + template.language + ")" : "")}
        </div>
        <span style={{ whiteSpace: "pre-wrap" }}>{template.body || caption || text}</span>
      </Bubble>
    );
  }

  if (msgtype === "interactive" || msgtype === "button") {
    const buttons = getMessageButtons(item);
    const body = text || payloadBody(item);
    blocks.push(
      <Bubble key="interactive" theme={theme} keyName={index + "interactive"} channel="interactive">
        <span style={{ whiteSpace: "pre-wrap" }}>{body}</span>
        {buttons.map((label, buttonIndex) => (
          <div
            key={"button" + buttonIndex}
            className="mt-1 text-center rounded-3"
            style={{ border: "1px solid " + theme.link, color: theme.link, fontSize: 11, padding: "2px 6px" }}
          >
            {label}
          </div>
        ))}
      </Bubble>
    );
  }

  if (msgtype === "reaction") {
    blocks.push(
      <Bubble key="reaction" theme={theme} keyName={index + "reaction"} channel="reaction">
        <span style={{ fontSize: 16 }}>{getMessageReaction(item)}</span>
        <span className="ms-2" style={{ color: theme.muted, fontSize: 10 }}>reaction</span>
      </Bubble>
    );
  }

  if (msgtype === "unknown") {
    const payload = parseMessagePayload(item?.payload);
    blocks.push(
      <Bubble key="unknown" theme={theme} keyName={index + "unknown"} channel="unknown">
        <span style={{ color: theme.muted, fontSize: 11 }}>
          {"Unsupported message" + (payload?.type ? " (" + payload.type + ")" : "")}
        </span>
        {media ? (
          <div>
            <a href={media} target="_blank" rel="noopener noreferrer" style={{ color: theme.link, textDecoration: "underline" }}>
              {filename || "Open attachment"}
            </a>
          </div>
        ) : null}
      </Bubble>
    );
  }

  // Caption so existe em midia RECEBIDA. Em mensagem enviada o texto fica em
  // "message" e o bubble de texto ja o exibe — renderizar os dois duplicava.
  const captionText = isMediaMessageType(msgtype) ? (caption || text) : "";

  if (captionText.trim()) {
    blocks.push(
      <Bubble key="caption" theme={theme} keyName={index + "caption"} channel="caption">
        <span style={{ whiteSpace: "pre-wrap" }}>{captionText}</span>
      </Bubble>
    );
  }

  return <>{blocks}</>;
}

function payloadBody(item) {
  const payload = parseMessagePayload(item?.payload);
  return payload?.text || payload?.body || "";
}
